import type { SecurityIssue } from './securityAnalysis';

export interface ThreatGroup {
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  threats: ThreatDetail[];
  totalOccurrences: number;
  totalCharacters: number;
}

export interface ThreatDetail {
  type: string;
  description: string;
  positions: number[];
  occurrences: number;
  characters: number;
  recommendation: string;
}

export interface LineBreakdownData {
  lineNumber: number;
  content: string;
  threats: {
    position: number;
    type: string;
    severity: string;
  }[];
  lineStartPos?: number;
}

export interface ThreatBreakdown {
  groups: ThreatGroup[];
  lineBreakdown: LineBreakdownData[];
  totalThreatCount: number;
  totalOccurrences: number;
  totalCharacters: number;
}

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export function formatThreats(issues: SecurityIssue[], text: string): ThreatBreakdown {
  // Group issues by severity and type
  const grouped = new Map<string, ThreatGroup>();

  issues.forEach((issue) => {
    const severity = (issue.severity.toUpperCase() as 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW');
    const key = severity;

    if (!grouped.has(key)) {
      grouped.set(key, {
        severity,
        threats: [],
        totalOccurrences: 0,
        totalCharacters: 0,
      });
    }

    const group = grouped.get(key)!;
    const threatType = issue.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

    // Find or create threat detail
    let threatDetail = group.threats.find((t) => t.type === threatType);
    if (!threatDetail) {
      threatDetail = {
        type: threatType,
        description: issue.description,
        positions: issue.positions || [],
        occurrences: issue.positions?.length || 1,
        characters: issue.positions?.length || 1,
        recommendation: issue.recommendation,
      };
      group.threats.push(threatDetail);
    } else {
      threatDetail.positions.push(...(issue.positions || []));
      threatDetail.occurrences = threatDetail.positions.length;
      threatDetail.characters = threatDetail.positions.length;
    }

    group.totalOccurrences += issue.positions?.length || 1;
    group.totalCharacters += issue.positions?.length || 1;
  });

  // Sort groups by severity
  const groups = Array.from(grouped.values()).sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]
  );

  // Sort threats within each group by occurrence count (descending)
  groups.forEach((group) => {
    group.threats.sort((a, b) => b.occurrences - a.occurrences);
  });

  // Generate line-by-line breakdown
  const lineBreakdown = generateLineBreakdown(text, issues);

  // Calculate totals
  const totalThreatCount = issues.length;
  const totalOccurrences = groups.reduce((sum, g) => sum + g.totalOccurrences, 0);
  const totalCharacters = groups.reduce((sum, g) => sum + g.totalCharacters, 0);

  return {
    groups,
    lineBreakdown,
    totalThreatCount,
    totalOccurrences,
    totalCharacters,
  };
}

function generateLineBreakdown(text: string, issues: SecurityIssue[]): LineBreakdownData[] {
  const lines = text.split('\n');
  const lineData: LineBreakdownData[] = [];

  let charPosition = 0;
  lines.forEach((line, lineIndex) => {
    const lineNumber = lineIndex + 1;
    const lineStart = charPosition;
    const lineEnd = charPosition + line.length;

    // Find threats that occur on this line
    const lineThreats = issues
      .flatMap((issue) =>
        (issue.positions || [])
          .filter((pos) => pos >= charPosition && pos < lineEnd)
          .map((pos) => ({
            position: pos,
            type: issue.type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
            severity: issue.severity,
          }))
      )
      .sort((a, b) => a.position - b.position);

    lineData.push({
      lineNumber,
      content: line,
      threats: lineThreats,
      lineStartPos: lineStart,
    });

    charPosition = lineEnd + 1; // +1 for newline character
  });

  return lineData;
}

export function getSeverityColor(severity: string): string {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return '#d32f2f'; // Red
    case 'HIGH':
      return '#f57c00'; // Orange
    case 'MEDIUM':
      return '#fbc02d'; // Yellow
    case 'LOW':
      return '#1976d2'; // Blue
    default:
      return '#757575'; // Gray
  }
}

export function getSeverityBadgeColor(
  severity: string
): 'red' | 'orange' | 'yellow' | 'blue' | 'grey' {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return 'red';
    case 'HIGH':
      return 'orange';
    case 'MEDIUM':
      return 'yellow';
    case 'LOW':
      return 'blue';
    default:
      return 'grey';
  }
}
